import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, FileText, Cpu, Usb, Server, Box, Layers, Monitor, Tablet, Smartphone, AudioWaveform, Archive, ChevronDown, ClipboardList, Loader2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SURVEY_KEY = 'wing_dl_survey_v1';

const MODELOS = ['WING Full', 'WING Compact', 'WING Rack', 'Ainda não tenho / Outro'];

// Release notes oficiais do firmware v3.1.2
const NOTES_312 = [
  'Alterado: melhor indicação de quando o controle de pan de um send fader está vinculado ao pan do canal.',
  'Corrigido: alguns cartões de expansão Dante não inicializavam ao usar módulos BK3 com firmware Dante 4.3.1.8.',
  'Corrigido: reordenar os slots de processamento do canal podia aumentar a latência.',
];

const trackDownload = (file) => {
  axios.post(`${BACKEND_URL}/api/downloads/track`, { file_name: file.name, versao: file.version }).catch(() => {});
};

// Estrutura de dados centralizada — fácil de atualizar.
// Quando você tiver os arquivos finais, basta substituir o `url` de cada item.
// Usamos `#` como placeholder até receber os links/arquivos.
const SECTIONS = [
  {
    id: 'wing-rack',
    title: 'WING Rack',
    subtitle: 'Console rackeável para sistemas fixos e mobile',
    icon: Server,
    files: [
      {
        name: 'Firmware WING Rack',
        version: 'v3.1.2',
        size: '32 MB',
        format: 'ZIP',
        url: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_1ab04b51-dc99-4928-9840-c35191803daa/artifacts/cyv0crhn_wing-rack-release-3.1.2-20260729.zip',
        updated: '07/2026',
        notes: NOTES_312,
      },
      {
        name: 'Manual do Usuário (PT-BR)',
        version: 'Rev. 1',
        size: '19 MB',
        format: 'PDF',
        url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/q38ievqh_Manual_WING%2C%20WING%20RACK%2C%20WING%20Compact-1.pdf',
        updated: '2026',
      },
      {
        name: 'Guia de Efeitos (Effects Guide)',
        version: 'Rev. WW-5',
        size: '13 MB',
        format: 'PDF',
        url: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_1ab04b51-dc99-4928-9840-c35191803daa/artifacts/mcm8xgxn_M_BE_0603-AEN_WING%20EFFECTS%20GUIDE%20DARK1_WW-5.pdf',
        updated: '2026',
      },
    ],
  },
  {
    id: 'wing-compact',
    title: 'WING Compact',
    subtitle: 'Versão portátil — ideal para shows itinerantes',
    icon: Box,
    files: [
      {
        name: 'Firmware WING Compact',
        version: 'v3.1.2',
        size: '32 MB',
        format: 'ZIP',
        url: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_1ab04b51-dc99-4928-9840-c35191803daa/artifacts/9om0rx5o_wing-compact-release-3.1.2-20260729.zip',
        updated: '07/2026',
        notes: NOTES_312,
      },
      {
        name: 'Manual do Usuário (PT-BR)',
        version: 'Rev. 1',
        size: '19 MB',
        format: 'PDF',
        url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/q38ievqh_Manual_WING%2C%20WING%20RACK%2C%20WING%20Compact-1.pdf',
        updated: '2026',
      },
      {
        name: 'Guia de Efeitos (Effects Guide)',
        version: 'Rev. WW-5',
        size: '13 MB',
        format: 'PDF',
        url: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_1ab04b51-dc99-4928-9840-c35191803daa/artifacts/mcm8xgxn_M_BE_0603-AEN_WING%20EFFECTS%20GUIDE%20DARK1_WW-5.pdf',
        updated: '2026',
      },
    ],
  },
  {
    id: 'wing-full',
    title: 'WING Full',
    subtitle: 'Console flagship com 24 faders motorizados',
    icon: Layers,
    files: [
      {
        name: 'Firmware WING Full',
        version: 'v3.1.2',
        size: '33 MB',
        format: 'ZIP',
        url: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_1ab04b51-dc99-4928-9840-c35191803daa/artifacts/flhq4i2i_wing-fullsize-release-3.1.2-20260729.zip',
        updated: '07/2026',
        notes: NOTES_312,
      },
      {
        name: 'Manual do Usuário (PT-BR)',
        version: 'Rev. 1',
        size: '19 MB',
        format: 'PDF',
        url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/q38ievqh_Manual_WING%2C%20WING%20RACK%2C%20WING%20Compact-1.pdf',
        updated: '2026',
      },
      {
        name: 'Guia de Efeitos (Effects Guide)',
        version: 'Rev. WW-5',
        size: '13 MB',
        format: 'PDF',
        url: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_1ab04b51-dc99-4928-9840-c35191803daa/artifacts/mcm8xgxn_M_BE_0603-AEN_WING%20EFFECTS%20GUIDE%20DARK1_WW-5.pdf',
        updated: '2026',
      },
    ],
  },
  {
    id: 'driver-usb',
    title: 'Driver USB',
    subtitle: 'Driver oficial Behringer para conexão USB-Audio',
    icon: Usb,
    files: [
      {
        name: 'Driver USB Windows (10/11)',
        version: 'v6.00.0',
        size: '5 MB',
        format: 'ZIP',
        url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/sducbxht_Wing%20-%20USB%20Driver%20v6.00.0%20%28windows%2010-11%29.zip',
        updated: '2026',
      },
      {
        name: 'Driver USB macOS',
        version: 'v5.61',
        size: '— MB',
        format: 'DMG',
        url: '#',
        updated: '2026',
      },
    ],
  },
  {
    id: 'wing-edit',
    title: 'WING EDIT App',
    subtitle: 'Software desktop para edição offline e mixagem remota',
    icon: Monitor,
    files: [
      {
        name: 'WING EDIT — Windows',
        version: 'v3.3.3',
        size: '87 MB',
        format: 'ZIP',
        url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/i13zz77i_Wing%20-%20Edit%203.3.3%20%28Windows%29.zip',
        updated: '2026',
      },
      {
        name: 'WING EDIT — macOS',
        version: 'v3.3.3',
        size: '153 MB',
        format: 'ZIP',
        url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/bh9o2tlz_Wing%20-%20Edit%203.3.3%20%28Mac%29.zip',
        updated: '2026',
      },
      {
        name: 'WING EDIT — Linux',
        version: 'v3.0',
        size: '— MB',
        format: 'ZIP',
        url: '#',
        updated: '2026',
      },
    ],
  },
  {
    id: 'copilot',
    title: 'CoPilot App',
    subtitle: 'Mixagem remota profissional via tablet (iOS / Android)',
    icon: Tablet,
    files: [
      {
        name: 'CoPilot — iPad (App Store)',
        version: 'Última',
        size: '—',
        format: 'LINK',
        url: '#',
        updated: '2026',
      },
      {
        name: 'CoPilot — Android (Play Store)',
        version: 'Última',
        size: '—',
        format: 'LINK',
        url: '#',
        updated: '2026',
      },
    ],
  },
  {
    id: 'wing-g',
    title: 'WING-G — Android',
    subtitle: 'Controle remoto da console pelo celular Android',
    icon: Smartphone,
    files: [
      {
        name: 'WING-G — Google Play',
        version: 'Última',
        size: '—',
        format: 'LINK',
        url: '#',
        updated: '2026',
      },
      {
        name: 'WING-G — APK direto',
        version: 'v1.0',
        size: '— MB',
        format: 'APK',
        url: '#',
        updated: '2026',
      },
    ],
  },
];

// Arquivo de versões anteriores (histórico)
const OLD_VERSIONS = [
  {
    name: 'Firmware WING Full',
    version: 'v3.1.1',
    size: '33 MB',
    format: 'ZIP',
    url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/sw3fs4h2_wing-fullsize-release-3.1.1-20260601%281%29.zip',
    updated: '06/2026',
  },
  {
    name: 'Firmware WING Compact',
    version: 'v3.1.1',
    size: '32 MB',
    format: 'ZIP',
    url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/hd0xgxd2_wing-compact-release-3.1.1.zip',
    updated: '06/2026',
  },
  {
    name: 'Firmware WING Rack',
    version: 'v3.1.1',
    size: '32 MB',
    format: 'ZIP',
    url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/hn62vfbp_wing-rack-release-3.1.1.zip',
    updated: '06/2026',
  },
  {
    name: 'Firmware WING Full',
    version: 'v3.1',
    size: '30 MB',
    format: 'RAR',
    url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/m6yqir59_WING%20FULLV%203.1%20.rar',
    updated: '2026',
  },
  {
    name: 'Firmware WING Compact',
    version: 'v3.1',
    size: '30 MB',
    format: 'RAR',
    url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/r85j8s41_WING%20COMPACT%20vers%C3%A3o%203.1%20.rar',
    updated: '2026',
  },
  {
    name: 'Firmware WING Rack',
    version: 'v3.1',
    size: '29 MB',
    format: 'RAR',
    url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/sspnuzfo_WING%20RACK%20%20FIRMEWARE%20VERS%C3%83O%203.1.rar',
    updated: '2026',
  },
  {
    name: 'WING EDIT — Windows',
    version: 'v3.3.2',
    size: '87 MB',
    format: 'ZIP',
    url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/xx6y71iy_Wing-Edit_PC_3.3.2%283%29.zip',
    updated: '2026',
  },
  {
    name: 'WING EDIT — macOS',
    version: 'v3.3.2',
    size: '153 MB',
    format: 'ZIP',
    url: 'https://customer-assets.emergentagent.com/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/dnthkn0s_Wing-Edit_MAC_3.3.2.zip',
    updated: '2026',
  },
];

const OLD_VERSIONS_306 = [
  {
    name: 'Firmware WING Full',
    version: 'v3.0.6',
    size: '29 MB',
    format: 'RAR',
    url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/4rxkmx9u_wing-Full%20-3.0.6.rar',
    updated: '2026',
  },
  {
    name: 'Firmware WING Compact',
    version: 'v3.0.6',
    size: '29 MB',
    format: 'RAR',
    url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/ad68n336_wing-Compact-V3.0.6.rar',
    updated: '2026',
  },
  {
    name: 'Firmware WING Rack',
    version: 'v3.0.6',
    size: '29 MB',
    format: 'RAR',
    url: 'https://customer-assets-eiarnc6j.emergentagent.net/job_207ce1a1-cc0d-4cea-b992-9f78cf5204d4/artifacts/8zyv671w_wing-Rack-%20V3.0.6.rar',
    updated: '2026',
  },
];

const formatBadge = (format) => {
  const map = {
    ZIP: 'bg-purple-100 text-purple-700 border-purple-200',
    PDF: 'bg-rose-100 text-rose-700 border-rose-200',
    EXE: 'bg-blue-100 text-blue-700 border-blue-200',
    DMG: 'bg-gray-100 text-gray-700 border-gray-300',
    APK: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    LINK: 'bg-amber-100 text-amber-700 border-amber-200',
    WINGFW: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    RAR: 'bg-orange-100 text-orange-700 border-orange-200',
  };
  return map[format] || 'bg-gray-100 text-gray-700 border-gray-300';
};

const FileRow = ({ file }) => {
  const isPlaceholder = !file.url || file.url === '#';
  const [showNotes, setShowNotes] = useState(false);
  const hasNotes = Array.isArray(file.notes) && file.notes.length > 0;
  return (
    <div
      data-testid={`download-file-${file.name.toLowerCase().replace(/\s+/g, '-')}`}
      className="bg-white rounded-xl border border-yellow-200 hover:border-yellow-400 hover:shadow-md transition-all"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-yellow-200">
            <FileText className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-gray-900 text-sm sm:text-base">{file.name}</div>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
              <span className={`inline-block px-2 py-0.5 rounded border font-bold ${formatBadge(file.format)}`}>
                {file.format}
              </span>
              <span>{file.version}</span>
              <span>•</span>
              <span>{file.size}</span>
              <span>•</span>
              <span>Atualizado em {file.updated}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {hasNotes && (
            <button
              onClick={() => setShowNotes((v) => !v)}
              data-testid={`download-notes-toggle-${file.version}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yellow-50 hover:bg-yellow-100 text-yellow-800 text-sm font-semibold border border-yellow-200 transition-all"
            >
              O que mudou?
              <ChevronDown className={`w-4 h-4 transition-transform ${showNotes ? 'rotate-180' : ''}`} />
            </button>
          )}
          {isPlaceholder ? (
            <span className="px-4 py-2 rounded-lg bg-gray-100 text-gray-400 text-sm font-semibold cursor-not-allowed border border-gray-200">
              Em breve
            </span>
          ) : (
            <a
              href={file.url}
              download
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackDownload(file)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black text-sm font-bold transition-all hover:scale-[1.02]"
            >
              <Download className="w-4 h-4" />
              Baixar
            </a>
          )}
        </div>
      </div>
      {hasNotes && showNotes && (
        <div className="px-4 pb-4 -mt-1" data-testid={`download-notes-${file.version}`}>
          <div className="rounded-lg bg-yellow-50/70 border border-yellow-200 p-4">
            <div className="text-sm font-bold text-gray-900 mb-2">Novidades da {file.version}</div>
            <ul className="space-y-1.5">
              {file.notes.map((n, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-yellow-500 flex-shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
            {file.notesSource && (
              <div className="mt-3 text-xs text-gray-500">Fonte: {file.notesSource}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SurveyModal = ({ onDone }) => {
  const [form, setForm] = useState({ nome: '', email: '', modelo: '', data_nascimento: '', onde_comprou: '', sugestao: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const podeEnviar = form.nome.trim().length >= 2 && emailValido && form.modelo && consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!podeEnviar) {
      setError('Preencha nome, e-mail válido, o modelo e aceite a Política de Privacidade.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/downloads/survey`, {
        nome: form.nome.trim(),
        email: form.email.trim(),
        modelo: form.modelo,
        data_nascimento: form.data_nascimento || null,
        onde_comprou: form.onde_comprou || null,
        sugestao: form.sugestao || null,
        consentimento: true,
      });
      try { localStorage.setItem(SURVEY_KEY, '1'); } catch (_) {}
      onDone();
    } catch (err) {
      setError('Não foi possível enviar. Tente novamente em instantes.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" data-testid="download-survey-modal">
      <div className="bg-white rounded-2xl border border-yellow-300 shadow-2xl w-full max-w-lg my-6">
        <div className="bg-black rounded-t-2xl px-6 py-5 border-b-4 border-yellow-400 flex items-center gap-3">
          <div className="w-11 h-11 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-6 h-6 text-black" />
          </div>
          <div>
            <h2 className="text-lg font-black text-white leading-tight">Antes de baixar, responda 1 minuto</h2>
            <p className="text-xs text-gray-300 mt-0.5">Ajuda a melhorar nossos conteúdos. Depois de enviar, todos os downloads ficam liberados.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Nome <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.nome}
              onChange={setField('nome')}
              data-testid="survey-nome"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
              placeholder="Seu nome completo"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">E-mail <span className="text-red-500">*</span></label>
            <input
              type="email"
              value={form.email}
              onChange={setField('email')}
              data-testid="survey-email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
              placeholder="voce@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Qual modelo da sua WING? <span className="text-red-500">*</span></label>
            <select
              value={form.modelo}
              onChange={setField('modelo')}
              data-testid="survey-modelo"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
            >
              <option value="">Selecione...</option>
              {MODELOS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Data de nascimento <span className="text-gray-400 font-normal">(opcional)</span></label>
            <input
              type="date"
              value={form.data_nascimento}
              onChange={setField('data_nascimento')}
              data-testid="survey-nascimento"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Onde você comprou sua WING? <span className="text-gray-400 font-normal">(opcional)</span></label>
            <input
              type="text"
              value={form.onde_comprou}
              onChange={setField('onde_comprou')}
              data-testid="survey-onde-comprou"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
              placeholder="Loja, site, cidade..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Sua sugestão de melhoria para o site <span className="text-gray-400 font-normal">(opcional)</span></label>
            <textarea
              value={form.sugestao}
              onChange={setField('sugestao')}
              data-testid="survey-sugestao"
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
              placeholder="Escreva o que gostaria de ver aqui..."
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="survey-error">{error}</div>
          )}

          <label className="flex items-start gap-3 cursor-pointer" data-testid="survey-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="survey-consent-checkbox"
              className="mt-1 w-4 h-4 accent-yellow-500 flex-shrink-0"
            />
            <span className="text-xs text-gray-500 leading-relaxed">
              Li e concordo com a{' '}
              <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="text-yellow-600 hover:underline">Política de Privacidade (LGPD)</a>.
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting || !podeEnviar}
            data-testid="survey-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold transition-all"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            {submitting ? 'Enviando...' : 'Enviar e liberar downloads'}
          </button>
          <p className="text-[11px] text-gray-400 text-center">Seus dados são tratados conforme nossa Política de Privacidade (LGPD).</p>
        </form>
      </div>
    </div>
  );
};

const Downloads = () => {
  const [unlocked, setUnlocked] = useState(true);

  useEffect(() => {
    let done = false;
    try { done = localStorage.getItem(SURVEY_KEY) === '1'; } catch (_) {}
    setUnlocked(done);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-50 text-gray-900">
      {!unlocked && <SurveyModal onDone={() => setUnlocked(true)} />}
      {/* Top Bar */}
      <header className="bg-black border-b-4 border-yellow-400 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="downloads-back-home"
            className="inline-flex items-center gap-2 text-gray-300 hover:text-yellow-400 font-semibold text-sm transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Voltar ao site</span>
          </Link>
          <Link
            to="/"
            data-testid="downloads-logo"
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo-wing-white.png"
              alt="WING Academy Brasil"
              className="h-10 sm:h-12 md:h-14 w-auto object-contain"
            />
          </Link>
          {/* Espaçador invisível para centralizar a logo */}
          <span className="w-[110px] hidden sm:inline-block" aria-hidden="true"></span>
          <span className="w-5 sm:hidden" aria-hidden="true"></span>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 pt-12 pb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-2xl mb-5 shadow-lg shadow-yellow-200">
          <Download className="w-8 h-8 text-black" />
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 leading-tight">
          Central de <span className="text-yellow-500">Downloads</span>
        </h1>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          Apps oficiais, firmwares e drivers para sua Console WING.
          Tudo <strong className="text-yellow-600">100% gratuito</strong> e direto da Behringer.
        </p>
      </section>

      {/* Sections */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto space-y-8">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <div
                key={section.id}
                data-testid={`download-section-${section.id}`}
                className="bg-white rounded-2xl border border-yellow-200 shadow-sm p-5 sm:p-7"
              >
                <div className="flex items-start gap-4 mb-5 pb-5 border-b border-yellow-100">
                  <div className="w-12 h-12 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-yellow-200">
                    <Icon className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                      {section.title}
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">{section.subtitle}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {section.files.map((file, idx) => (
                    <FileRow key={`${section.id}-${idx}`} file={file} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Box: Versões Anteriores (arquivo) */}
        <div className="max-w-4xl mx-auto mt-8">
          <details
            data-testid="old-versions-box"
            className="group bg-gray-50 rounded-2xl border border-gray-300 shadow-sm overflow-hidden"
          >
            <summary className="cursor-pointer list-none p-5 sm:p-7 flex items-center justify-between gap-4 hover:bg-gray-100 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Archive className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                    Versões Anteriores — v3.1.1 e v3.1
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Firmwares v3.1.1 e v3.1 (Full, Compact e Rack) e WING EDIT v3.3.2
                  </p>
                </div>
              </div>
              <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-5 sm:px-7 pb-5 sm:pb-7">
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                ⚠️ Use versões antigas apenas se precisar de compatibilidade específica. Recomendamos sempre a versão mais recente.
              </div>
              <div className="space-y-3">
                {OLD_VERSIONS.map((file, idx) => (
                  <FileRow key={`old-${idx}`} file={file} />
                ))}
              </div>
            </div>
          </details>
        </div>

        {/* Box: Versões Anteriores v3.0.6 (arquivo) */}
        <div className="max-w-4xl mx-auto mt-6">
          <details
            data-testid="old-versions-306-box"
            className="group bg-gray-50 rounded-2xl border border-gray-300 shadow-sm overflow-hidden"
          >
            <summary className="cursor-pointer list-none p-5 sm:p-7 flex items-center justify-between gap-4 hover:bg-gray-100 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Archive className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                    Versões Anteriores — v3.0.6
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Firmwares v3.0.6 para WING Full, Compact e Rack
                  </p>
                </div>
              </div>
              <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-5 sm:px-7 pb-5 sm:pb-7">
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                ⚠️ Use versões antigas apenas se precisar de compatibilidade específica. Recomendamos sempre a versão mais recente.
              </div>
              <div className="space-y-3">
                {OLD_VERSIONS_306.map((file, idx) => (
                  <FileRow key={`old306-${idx}`} file={file} />
                ))}
              </div>
            </div>
          </details>
        </div>

        {/* Info final */}
        <div className="max-w-4xl mx-auto mt-10 p-5 sm:p-6 bg-yellow-100/60 border border-yellow-300 rounded-2xl flex items-start gap-4">
          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Cpu className="w-5 h-5 text-black" />
          </div>
          <div className="text-sm text-gray-700">
            <strong className="text-gray-900">Atenção:</strong> sempre verifique o modelo correto da
            sua console antes de instalar firmware. Em caso de dúvidas, consulte o manual ou entre em
            contato pela página principal.
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/"
            data-testid="downloads-back-home-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-black text-white font-bold transition-all hover:scale-[1.02]"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar para a WING Academy
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Downloads;
